'use client';
import {Calendar, CheckCircle2, Info, ShieldCheck, Loader2} from 'lucide-react';
import {Card, CardContent} from '@/shadcn/components/ui/card';
import {useState} from 'react';
import {Input} from '@/shadcn/components/ui/input';
import {subscribeToNewsletter} from "@/app/actions/newsletter-actions";
import {getTurnstileToken} from "@/app/actions/turnstile-client";
import {optimisticManager} from "@/lib/optimistic-loading";

export default function Newsletter() {
    const [email, setEmail] = useState('');
    // const [agreed, setAgreed] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [error, setError] = useState<null | undefined | string>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        if (!email) {
            setError('Bitte gib eine gültige E-Mail-Adresse ein.');
            setLoading(false);
            return;
        }

        // Show loading briefly, then immediate optimistic success feedback
        setTimeout(() => {
            setLoading(false);
            setSubmitted(true);
        }, 500); // Brief loading state for UX
        
        // Execute background newsletter subscription with optimistic loading
        const taskId = `newsletter-${email}-${Date.now()}`;
        
        optimisticManager.executeOptimistic(
            taskId,
            { email, subscribed: true },
            async () => {
                const token = await getTurnstileToken();
                const result = await subscribeToNewsletter(email, true, token);
                
                if (!result.success) {
                    throw new Error(result?.message || 'Ein Fehler ist aufgetreten.');
                }
            },
            {
                notifyOnError: true,
                taskName: 'Newsletter Subscription',
                context: { 
                    email: email,
                    source: 'Newsletter Component'
                }
            }
        );
    };

    return (
        <Card className="w-full max-w-(--breakpoint-3xl) mx-auto mt-4 p-0">
            <CardContent>
                <div className="bg-white py-12">
                    <div className="mx-auto max-w-7xl">
                        <div className="mx-auto grid grid-cols-1 lg:max-w-none lg:grid-cols-2">
                            <div className="max-w-xl lg:max-w-lg">
                                <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.18em] text-amber-700">
                                    <span className="inline-block h-px w-8 bg-amber-500" aria-hidden="true"/>
                                    Newsletter
                                </div>
                                <h2 className="mt-5 font-black tracking-tight text-3xl text-slate-900 sm:text-4xl lg:text-5xl lg:leading-[1.05]">
                                    Gute Einrichtungs-Ideen, einmal pro Woche.
                                </h2>
                                <p className="mt-6 text-base leading-[1.7] text-slate-700 sm:text-lg">
                                    Keine Werbeflut, keine täglichen „Top-Deals". Nur ein redaktionelles
                                    Wochenupdate mit Einrichtungs-Ideen, Produktempfehlungen und
                                    Preisalarmen aus unserer Datenbank.
                                </p>
                                {submitted ? (
                                    <div className="mt-6 flex items-center text-green-600">
                                        <CheckCircle2 className="h-6 w-6 mr-2"/>
                                        <p className="text-base">
                                            Vielen Dank für deine Anmeldung. Bitte prüfe deine E-Mails, um die Anmeldung zu bestätigen.
                                        </p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="mt-8 max-w-md">
                                        <label htmlFor="email-address" className="sr-only">
                                            E-Mail-Adresse
                                        </label>
                                        <Input
                                            id="email-address"
                                            name="email"
                                            type="email"
                                            required
                                            placeholder="Deine E-Mail-Adresse"
                                            autoComplete="email"
                                            className="mb-4 text-base"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                        {error && <p className="text-red-500 mb-4">{error}</p>}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="inline-flex h-11 items-center justify-center rounded-md bg-amber-500 active:bg-amber-700 px-6 text-sm font-semibold text-white shadow-xs hover:bg-amber-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                            {loading ? 'Anmeldung läuft' : 'Anmelden'}
                                        </button>
                                        <p className="mt-3 text-xs text-slate-500">
                                            Kein Spam. Abmeldung jederzeit mit einem Klick.
                                        </p>
                                    </form>
                                )}
                            </div>
                            <div className="hidden md:block lg:pl-10 lg:pt-24">
                                <dl className="divide-y divide-slate-200 border-y border-slate-200">
                                    <div className="py-5">
                                        <dt className="text-base font-semibold text-slate-900">Einmal pro Woche</dt>
                                        <dd className="mt-1.5 text-sm leading-[1.7] text-slate-600">
                                            Immer freitags, kurz und mit Bild. Nichts dazwischen.
                                        </dd>
                                    </div>
                                    <div className="py-5">
                                        <dt className="text-base font-semibold text-slate-900">Redaktionell, nicht werblich</dt>
                                        <dd className="mt-1.5 text-sm leading-[1.7] text-slate-600">
                                            Unsere Redaktion pickt Produkte und Themen, nicht unser
                                            Verkaufsalgorithmus.
                                        </dd>
                                    </div>
                                    <div className="py-5">
                                        <dt className="text-base font-semibold text-slate-900">Preisalarme inklusive</dt>
                                        <dd className="mt-1.5 text-sm leading-[1.7] text-slate-600">
                                            Wenn ein Produkt aus deiner Favoritenliste reduziert wird,
                                            erfährst du es im Update.
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
